import React, { useState } from 'react';
import { ChevronRight, Search, ArrowUpDown, ArrowUp, ArrowDown, X, AlertCircle } from 'lucide-react';
import api from '../apiConfig/api';
import * as XLSX from 'xlsx';

// Reusable components
const LoadingRows = () => (
  <div className="p-4 animate-pulse space-y-3">
    {[1, 2, 3].map(i => (
      <div key={i} className="grid grid-cols-5 gap-4">
        <div className="h-4 bg-gray-200 rounded col-span-2"></div>
        <div className="h-4 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded"></div>
        <div className="h-4 bg-gray-200 rounded"></div>
      </div>
    ))}
  </div>
);

// Asset Details Modal Component
const AssetDetailsModal = ({ isOpen, onClose, assetDetails, isLoading, wardInfo, onAssetClick, status }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-800">
            Assets Details - {wardInfo?.ward_name || 'N/A'} - {status === 'completed' ? 'Survey Completed' : 'Survey Pending'}
          </h2>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="overflow-auto p-4">
          {isLoading ? (
            <LoadingRows />
          ) : assetDetails.length > 0 ? (
            <div className="grid gap-4">
              {assetDetails.map((asset, index) => (
                <div 
                  key={index} 
                  onClick={() => status === 'completed' ? onAssetClick(asset.id) : null}
                  className={`border border-gray-200 rounded-lg p-4 transition-all ${
                    status === 'completed' 
                      ? 'hover:shadow-lg hover:border-gray-300 cursor-pointer' 
                      : 'cursor-default'
                  }`}
                >
                  <div className="grid grid-cols-2 gap-4">
  <div>
    <p className="text-sm text-gray-500">Street Name</p>
    <p className="font-medium">{asset.street_name || 'N/A'}</p>
  </div>
  <div>
    <p className="text-sm text-gray-500">Owner Name</p>
    <p className="font-medium">{asset.owner || 'N/A'}</p>
  </div>
  <div>
    <p className="text-sm text-gray-500">EB Number</p>
    <p className="font-medium">{asset.eb_no || 'N/A'}</p>
  </div>
  <div>
    <p className="text-sm text-gray-500">Status</p>
    <p className={`font-medium ${
      status === 'completed' ? 'text-green-600' : 'text-orange-600'
    }`}>
      {status === 'completed' ? 'Completed' : 'Pending'}
    </p>
  </div>
  {status === 'completed' && asset.created_at && (
    <>
      <div>
        <p className="text-sm text-gray-500">Created Date</p>
        <p className="font-medium text-sm">
          {new Date(asset.created_at).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit', 
            year: 'numeric'
          }) + ' ' + 
          new Date(asset.created_at).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })}
        </p>
      </div>
      <div>
        <p className="text-sm text-gray-500">Usage</p>
        <p className={`font-medium ${
          asset.usage?.toLowerCase() === 'commercial' || 
          asset.usage?.toLowerCase() === 'mixed' 
            ? 'text-red-600' 
            : 'text-gray-800'
        }`}>
          {asset.usage || 'N/A'}
        </p>
      </div>
    </>
  )}
</div>
                  {status === 'completed' && (
                    <div className="mt-2 flex justify-end text-sm text-blue-600">
                      View Details <ChevronRight className="h-4 w-4" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No asset details available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Organization Details Modal Component
const OrgDetailsModal = ({ isOpen, onClose, orgName, orgDetails, detailsLoading, onCompletedClick, onPendingClick }) => {
  if (!isOpen) return null;

  // Calculate totals for the summary row
  const totalCompleted = orgDetails.reduce((sum, detail) => sum + (detail.completed || 0), 0);
  const totalPending = orgDetails.reduce((sum, detail) => sum + (detail.active || 0), 0);
  const totalSum = totalCompleted + totalPending;

  // Excel download function for organization details
const downloadOrgExcel = () => {
  // Create title and date
  const currentDate = new Date().toLocaleDateString('en-GB');
  const title = `DIRECTORATE OF MUNICIPAL ADMINISTRATION – EB – GST – CORRELATION STATUS REPORT AS ON DATE ${currentDate}`;

  const wsData = [
    [title], // Title row
    [], // Empty row
    ['Zone Name', 'Ward Name', 'Completed', 'Pending', 'Total']
  ];
  
  // Add data rows
  orgDetails.forEach(detail => {
    wsData.push([
      detail.zone_name || 'N/A',
      detail.ward_name || 'N/A',
      detail.completed || 0,
      detail.active || 0,
      detail.completed + detail.active
    ]);
  });
  
  // Add summary row
  wsData.push(['TOTAL', '', totalCompleted, totalPending, totalSum]);

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  ws['!cols'] = [
    { width: 25 },
    { width: 25 },
    { width: 12 },
    { width: 12 },
    { width: 12 }
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Organization Details');
  XLSX.writeFile(wb, `DIRECTORATE OF MUNICIPAL ADMINISTRATION – EB – GST – CORRELATION STATUS REPORT AS ON DATE ${currentDate}.xlsx`);
};

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-800">
            {orgName} - Organization Details
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadOrgExcel}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              title="Download Organization Details"
            >
              <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Excel
            </button>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>
        <div className="overflow-auto">
          <div className="bg-gray-50 rounded-lg overflow-hidden min-w-[640px] m-4">
            {detailsLoading ? (
              <LoadingRows />
            ) : (
              <>
                <div className="grid grid-cols-5 gap-4 py-2 px-4 bg-gray-100 text-sm font-medium text-gray-500">
                  <div>Zone Name</div>
                  <div>Ward Name</div>
                  <div className="text-center">Completed</div>
                  <div className="text-center">Pending</div>
                  <div className="text-center">Total</div>
                </div>
                <div className="divide-y divide-gray-200">
                  {orgDetails.map((detail, index) => (
                    <div key={index} className="grid grid-cols-5 gap-4 py-3 px-4 text-sm">
                      <div className="text-gray-900 break-words">{detail.zone_name || 'N/A'}</div>
                      <div className="text-gray-900 break-words">{detail.ward_name || 'N/A'}</div>
                      <div className="text-center">
                        {detail.completed > 0 ? (
                          <button
                            onClick={() => onCompletedClick(detail)}
                            className="text-green-600 font-medium hover:text-green-700 transition-colors hover:underline"
                          >
                            {detail.completed}
                          </button>
                        ) : (
                          <span className="text-gray-400 font-medium">0</span>
                        )}
                      </div>
                      <div className="text-center">
                        {detail.active > 0 ? (
                          <button
                            onClick={() => onPendingClick(detail)}
                            className="text-blue-600 font-medium hover:text-blue-700 transition-colors hover:underline"
                          >
                            {detail.active}
                          </button>
                        ) : (
                          <span className="text-gray-400 font-medium">0</span>
                        )}
                      </div>
                      <div className="text-center">
                        <span className="text-gray-800 font-medium">
                          {detail.completed + detail.active}
                        </span>
                      </div>
                    </div>
                  ))}
                  {/* Summary Row */}
                  <div className="grid grid-cols-5 gap-4 py-3 px-4 text-sm bg-gray-100 font-medium">
                    <div className="text-gray-900 col-span-2">Total</div>
                    <div className="text-center text-green-600">
                      {totalCompleted}
                    </div>
                    <div className="text-center text-blue-600">
                      {totalPending}
                    </div>
                    <div className="text-center text-gray-800">
                      {totalSum}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const OrgSummaryTable = ({ data, isLoading, onNavigateToReport }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null
  });
  
  // Modal states
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [selectedOrgName, setSelectedOrgName] = useState(null);
  const [selectedOrgCode, setSelectedOrgCode] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [orgDetails, setOrgDetails] = useState([]);
  
  // Asset modal states
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [selectedWardInfo, setSelectedWardInfo] = useState(null);
  const [assetDetails, setAssetDetails] = useState([]);
  const [isLoadingAssetDetails, setIsLoadingAssetDetails] = useState(false);
  const [assetModalStatus, setAssetModalStatus] = useState('completed');

  // Fix: Correctly calculate active from total and completed
  const groupedData = data.reduce((acc, item) => {
    if (!acc[item.org_name]) {
      acc[item.org_name] = {
        completed: 0,
        active: 0,
        total: 0,
        org_code: item.org_code // Store org_code for API calls
      };
    }
    
    // Convert string values to integers
    const completed = parseInt(item.completed || 0);
    const total = parseInt(item.total || 0);
    
    // Fix: Calculate active as the difference between total and completed
    const active = total - completed;
    
    acc[item.org_name].completed += completed;
    acc[item.org_name].active += active;
    acc[item.org_name].total += total;
    acc[item.org_name].org_code = item.org_code; // Ensure org_code is set
    
    return acc;
  }, {});

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleCompletedClick = async (detail) => {
    setSelectedWardInfo(detail);
    setIsAssetModalOpen(true);
    setIsLoadingAssetDetails(true);
    setAssetModalStatus('completed');

    try {
      const response = await api.get('/asset/report', {
        params: {
          org: detail.org,
          zone: detail.zone_name,
          ward: detail.ward_name,
          status: 'completed'
        }
      });
      setAssetDetails(response.data.data || []);
    } catch (error) {
      console.error('Error fetching asset details:', error);
      setAssetDetails([]);
    } finally {
      setIsLoadingAssetDetails(false);
    }
  };

  const handlePendingClick = async (detail) => {
    setSelectedWardInfo(detail);
    setIsAssetModalOpen(true);
    setIsLoadingAssetDetails(true);
    setAssetModalStatus('active');

    try {
      const response = await api.get('/asset/report', {
        params: {
          org: detail.org_code,
          zone: detail.zone_name,
          ward: detail.ward_name,
          status: 'active'
        }
      });
      setAssetDetails(response.data.data || []);
    } catch (error) {
      console.error('Error fetching asset details:', error);
      setAssetDetails([]);
    } finally {
      setIsLoadingAssetDetails(false);
    }
  };

  const handleAssetClick = (assetId) => {
    if (!assetId) return;
    onNavigateToReport(assetId);
  };

  // Add search functionality near sort buttons 
  const filteredOrgs = Object.entries(groupedData)
    .filter(([orgName]) => 
      orgName.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      const aValue = a[1][sortConfig.key] || 0;
      const bValue = b[1][sortConfig.key] || 0;
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

  const handleOrgClick = async (orgName, orgCode, status = null, zone = null, ward = null) => {
    setSelectedOrgName(orgName);
    setSelectedOrgCode(orgCode);
    setIsOrgModalOpen(true);
    setDetailsLoading(true);
    
    try {
      const params = {
        org: orgCode // Should be 'org' as parameter name, not 'org_code'
      };
      
      // Add status parameter if provided
      if (status) {
        params.status = status;
      }
      
      // Add zone and ward parameters if provided
      if (zone) {
        params.zone = zone;
      }
      
      if (ward) {
        params.ward = ward;
      }
      
      const response = await api.get('/user/asst_smry', {
        params: params
      });
      
      // Transform the data to correctly calculate active
      const transformedData = (response.data.data || []).map(detail => {
        const completed = parseInt(detail.completed || 0);
        const total = parseInt(detail.total || 0);
        // Calculate active correctly
        const active = total - completed;
        
        return {
          ...detail,
          completed,
          active,
          total,
          org_code: orgCode // Add org_code to each detail
        };
      });
      
      setOrgDetails(transformedData);
    } catch (error) {
      console.error('Error fetching organization details:', error);
      setOrgDetails([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return <ArrowUpDown className="h-4 w-4" />;
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="h-4 w-4" /> : 
      <ArrowDown className="h-4 w-4" />;
  };

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white p-4 rounded-lg shadow-sm">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

const downloadExcel = () => {
  // Calculate totals
  let totalSum = 0;
  let completedSum = 0;
  let pendingSum = 0;
  
  filteredOrgs.forEach(([orgName, orgData]) => {
    totalSum += orgData.total;
    completedSum += orgData.completed;
    pendingSum += orgData.active;
  });

  // Create title and date
  const currentDate = new Date().toLocaleDateString('en-GB');
  const title = `DIRECTORATE OF MUNICIPAL ADMINISTRATION – EB – GST – CORRELATION STATUS REPORT AS ON DATE ${currentDate}`;

  // Create worksheet data
  const wsData = [
    [title], // Title row
    [], // Empty row
    ['Organization Name', 'Total Count', 'Completed Count', 'Pending Count']
  ];
  
  // Add data rows
  filteredOrgs.forEach(([orgName, orgData]) => {
    wsData.push([orgName, orgData.total, orgData.completed, orgData.active]);
  });
  
  // Add summary row
  wsData.push(['TOTAL', totalSum, completedSum, pendingSum]);

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  ws['!cols'] = [
    { width: 40 },
    { width: 15 },
    { width: 18 },
    { width: 15 }
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Organizations Summary');
  XLSX.writeFile(wb, `DIRECTORATE OF MUNICIPAL ADMINISTRATION – EB – GST – CORRELATION STATUS REPORT AS ON DATE ${currentDate}.xlsx`);
};
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-800">Organizations Summary</h2>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleSort('completed')}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
              >
                Completed <SortIcon field="completed" />
              </button>
              <button
                onClick={() => handleSort('active')}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
              >
                Pending <SortIcon field="active" />
              </button>
              <button
                onClick={() => handleSort('total')}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
              >
                Total <SortIcon field="total" />
              </button>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <div className="relative w-full md:w-auto">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search organizations..."
                className="w-full md:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
            </div>
            <button
              onClick={downloadExcel}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              title="Download Excel"
            >
              <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
          </div>
        </div>

        {filteredOrgs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No organizations found matching "{searchTerm}"
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredOrgs.map(([orgName, orgData]) => (
              <div key={orgName}>
                <button
                  onClick={() => handleOrgClick(orgName, orgData.org_code)}
                  className="w-full flex flex-col md:flex-row md:items-center justify-between py-4 hover:bg-gray-50 transition-colors gap-2 md:gap-4"
                >
                  <div className="flex items-center gap-2">
                    <ChevronRight className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    <h3 className="font-medium text-gray-900 text-left">{orgName}</h3>
                  </div>
                  <div className="flex items-center gap-4 pl-7 md:pl-0">
                    <div className="text-sm">
                      <span className="text-gray-500 mr-2">Completed:</span>
                      {orgData.completed > 0 ? (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOrgClick(orgName, orgData.org_code, 'completed');
                          }}
                          className="text-green-600 font-medium hover:text-green-700 hover:underline"
                        >
                          {orgData.completed}
                        </button>
                      ) : (
                        <span className="text-gray-400 font-medium">0</span>
                      )}
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-500 mr-2">Pending:</span>
                      {orgData.active > 0 ? (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOrgClick(orgName, orgData.org_code, 'active');
                          }}
                          className="text-blue-600 font-medium hover:text-blue-700 hover:underline"
                        >
                          {orgData.active}
                        </button>
                      ) : (
                        <span className="text-gray-400 font-medium">0</span>
                      )}
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-500 mr-2">Total:</span>
                      <span className="text-gray-800 font-medium">{orgData.total}</span>
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Organization Details Modal */}
      <OrgDetailsModal
        isOpen={isOrgModalOpen}
        onClose={() => {
          setIsOrgModalOpen(false);
          setSelectedOrgName(null);
          setSelectedOrgCode(null);
          setOrgDetails([]);
        }}
        orgName={selectedOrgName}
        orgDetails={orgDetails}
        detailsLoading={detailsLoading}
        onCompletedClick={handleCompletedClick}
        onPendingClick={handlePendingClick}
      />

      {/* Asset Details Modal */}
      <AssetDetailsModal 
        isOpen={isAssetModalOpen}
        onClose={() => {
          setIsAssetModalOpen(false);
          setSelectedWardInfo(null);
          setAssetDetails([]);
        }}
        assetDetails={assetDetails}
        isLoading={isLoadingAssetDetails}
        wardInfo={selectedWardInfo}
        onAssetClick={handleAssetClick}
        status={assetModalStatus}
      />
    </div>
  );
};